import {
  Component,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { from, Observable, Subscription } from 'rxjs';
import { first, map, delayWhen } from 'rxjs/operators';
import { subscriptions, paymentMethods, IStripeSource } from 'stripe';
import { Entry } from 'contentful';

import { environment } from '../../../environments/environment';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { FunctionsService } from '../../services/functions/functions.service';
import { LocalizationService } from '../../services/localization/localization.service';
import { StoreService } from '../../services/store/store.service';
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { ProfileService } from '../../services/profile/profile.service';
import { ScriptService } from '../../services/script/script.service';
import { SubscriptionState } from '../../services/profile/interfaces';
import { Country, DEFAULT_COUNTRY } from '../../interfaces/country';
import { GlobalContentService } from '../../services/global-content/global-content.service';

declare var Stripe: stripe.StripeStatic;

@Component({
  selector: 'app-pay',
  templateUrl: './pay.component.html',
  styleUrls: ['./pay.component.scss'],
})
export class PayComponent implements OnInit, OnDestroy {
  @Input() public content: Entry<any> | undefined;

  public defaultPaymentMethod$: Observable<
    paymentMethods.IPaymentMethod | IStripeSource
  >;
  public paymentForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    address: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    postalCode: new FormControl('', [Validators.required]),
    legalAgreement: new FormControl(false, [
      Validators.required,
      Validators.requiredTrue,
    ]),
    stripe: new FormControl(false, [
      Validators.required,
      Validators.requiredTrue,
    ]),
    updatedTerms: new FormControl(true, [
      Validators.required,
      Validators.requiredTrue,
    ]),
  });

  public stripe: stripe.Stripe;
  public defaultPaymentMethod: paymentMethods.IPaymentMethod | IStripeSource;
  public card: any;
  public cardErrors: any;
  public selectedPlan = this.store.selectedPlan.value;
  public selectedPlanQuantity = this.store.selectedPlanQuantity.value;
  public selectedPlanTotal = this.store.selectedPlanTotal;

  public paymentComplete: boolean = false;
  public paymentErrors: null | string = null;
  public loading: boolean = false;
  public addPaymentMethod: boolean = false;
  public hasDefaultPaymentMethod: boolean = false;

  public defaultCountry: Country;

  public style = {
    base: {
      // Add your base input styles here. For example:
      fontSize: '16px',
      background: 'red',
      theme: 'dark',
      fontFamily: '"Circular", Helvetica',
    },
  };
  public showUpdatedTermsControl: boolean = false;

  private priceChangeDate: number = new Date('2020-04-25').getTime();
  private subscriptions: Subscription[] = [];

  public globalContent$: Observable<Entry<any>>;

  constructor(
    private store: StoreService,
    private fnService: FunctionsService,
    private analyticsService: AnalyticsService,
    private localizationService: LocalizationService,
    private profileService: ProfileService,
    private scriptService: ScriptService,
    private globalContentService: GlobalContentService,
  ) {
    this.defaultPaymentMethod$ = profileService.defaultPaymentMethod();
  }

  @ViewChild('cardElement', { static: true }) cardElement:
    | ElementRef
    | undefined;

  async ngOnInit() {
    this.globalContent$ = this.globalContentService.getGlobalContent();

    const stripeLocale =
      this.localizationService.locale.value === 'en-US'
        ? 'en'
        : this.localizationService.locale.value;

    if (!this.cardElement) {
      throw new Error('#cardElement not found');
    }

    // Set default country
    if (this.store.country.value) {
      this.defaultCountry = this.store.country.value;
    } else {
      this.defaultCountry = DEFAULT_COUNTRY;
    }
    this.paymentForm.get('country').setValue(this.defaultCountry.country_code);

    // Use name in profile as default name for user
    const userSnapshot = await this.profileService
      .user()
      .pipe(first())
      .toPromise();
    if (userSnapshot.firstName && userSnapshot.lastName) {
      this.paymentForm
        .get('name')
        .setValue(userSnapshot.firstName + ' ' + userSnapshot.lastName);
    }

    this.scriptService
      .load('stripe')
      .then(() => {
        this.stripe = Stripe(environment.stripe.public_api_key, {
          locale: stripeLocale,
        });
        const elements = this.stripe.elements();
        this.card = elements.create('card', {
          style: this.style,
          hidePostalCode: true,
        });
        this.card.mount(this.cardElement.nativeElement);
        this.card.addEventListener('change', (event: any) => {
          this.paymentErrors = null;
          const error = event.error;
          const complete = event.complete;
          const stripeControl = this.paymentForm.get('stripe'); //TODO: Is this used anywhere?

          if (stripeControl) {
            if (!error && complete) {
              stripeControl.setValue(true);
            } else {
              stripeControl.setValue(false);
            }
          }

          this.cardErrors = error ? error.message : null;
        });
      })
      .catch((error) => console.error(error));

    this.subscriptions.push(
      this.paymentForm.valueChanges.subscribe((value) => {
        if (this.paymentForm.valid) {
          this.paymentErrors = null; // Hide errors when form is valid
        }
      }),
    );

    this.subscriptions.push(
      this.defaultPaymentMethod$.subscribe((paymentMethod) => {
        this.hasDefaultPaymentMethod = true;
        this.defaultPaymentMethod = paymentMethod;

        const stripeControl = this.paymentForm.get('stripe');
        stripeControl.setValue(true);
      }),
    );

    // does the user have to accept new terms of service?
    if (userSnapshot.stripeCustomerId) {
      let needToShow = false;
      await this.profileService
        .getLastCancelledSubscription()
        .then((subscriptions) => {
          subscriptions.data.forEach((sub) => {
            if (sub.start_date * 1000 <= this.priceChangeDate) {
              needToShow = true;
            }
          });
        })
        .catch((error) => {
          console.error(error);
        });
      if (needToShow) {
        this.showUpdatedTermsControl = true;
        this.paymentForm.get('updatedTerms').setValue(false);
      } else {
        this.showUpdatedTermsControl = false;
      }
    }

    this.selectedPlanTotal = this.store.selectedPlanTotal;
  }

  get legalAgreement() {
    return this.paymentForm.get('legalAgreement');
  }

  get updatedTerms() {
    return this.paymentForm.get('updatedTerms');
  }

  countryChanged(country) {
    this.store.country.next(country);
    this.paymentForm.get('country').setValue(country.country_code);
  }

  validate() {
    Object.values(this.paymentForm.controls).forEach((control) => {
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }

  addNewPaymentMethod() {
    this.addPaymentMethod = true;
    const stripeControl = this.paymentForm.get('stripe');
    stripeControl.setValue(false);
  }

  async pay(e: Event) {
    e.preventDefault();

    this.validate();

    if (this.loading) {
      return;
    }

    if (this.paymentForm.valid) {
      this.loading = true;
      this.paymentErrors = null;

      const selectedPlan = this.store.selectedPlan.value;
      const selectedPlanQuantity = this.store.selectedPlanQuantity.value;
      const selectedPlanTotal = this.store.selectedPlanTotal.value;
      const selectedPlanCo2e = this.store.selectedPlanCo2e.value;

      const selectedPlanData = {
        selectedPlanQuantity,
        selectedPlanTotal,
        selectedPlanCo2e,
      };

      if (!selectedPlanQuantity || !selectedPlan || !selectedPlanTotal) {
        throw new Error();
      }

      if (!this.hasDefaultPaymentMethod || this.addPaymentMethod) {
        const { paymentMethod, error } = await this.stripe.createPaymentMethod(
          'card',
          this.card,
          {
            billing_details: {
              address: {
                city: this.paymentForm.get('city').value,
                country: this.paymentForm.get('country').value,
                line1: this.paymentForm.get('address').value,
                // line2: null, //TODO
                postal_code: this.paymentForm.get('postalCode').value,
                //state: null //TODO
              },
              name: this.paymentForm.get('name').value,
            },
          },
        );

        if (error) {
          this.paymentError(error.message);
        } else {
          this.createSubscription(
            paymentMethod,
            selectedPlan,
            selectedPlanData,
          );
        }
      } else {
        this.createSubscription(
          this.defaultPaymentMethod,
          selectedPlan,
          selectedPlanData,
        );
      }
    } else {
      this.paymentError(this.content.fields.errorWhenFormIsNotValid);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  hasError(formControl: AbstractControl, validator: string) {
    return (
      formControl.touched &&
      formControl.dirty &&
      formControl.hasError(validator)
    );
  }

  private async createSubscription(paymentMethod, selectedPlan, planData) {
    try {
      const { id } = await this.fnService.createSubscription({
        payment_method: paymentMethod.id,
        plan: selectedPlan.id,
        quantity: planData.selectedPlanQuantity,
        metadata: {
          planKey: selectedPlan.key,
          totalPrice: (planData.selectedPlanTotal * 12).toString(),
          co2e: Math.round(planData.selectedPlanCo2e).toString(),
        },
      });
      const subscription = await this.completeSubscription(id);
      this.paymentSuccess(subscription);
    } catch (e) {
      this.paymentError(
        this.content.fields.errorWhenTryingToMakeSubscription,
        e.message === 'StripeError' ? e.details : e,
      );
    }
  }

  private completeSubscription(id: string) {
    return this.profileService
      .subscriptionLifecycle(id)
      .pipe(
        delayWhen((state) => from(this.subscriptionState(state))),
        first(({ subscription }) => subscription.status === 'active'),
        map((state) => state.subscription),
      )
      .toPromise();
  }

  private async subscriptionState(state: SubscriptionState) {
    if (
      state.invoice.status === 'open' &&
      (state.subscription.status === 'incomplete' ||
        state.subscription.status === 'past_due')
    ) {
      await this.handleOpenInvoice(state);
    }
  }

  private async handleOpenInvoice(state: SubscriptionState) {
    if (state.paymentIntent.status === 'requires_payment_method') {
      const { type, code, message } = state.paymentIntent.last_payment_error;
      throw { type, code, message };
    } else if (state.paymentIntent.status === 'requires_action') {
      const clientSecret = state.paymentIntent.client_secret;
      const { error } = await this.stripe.handleCardPayment(clientSecret);
      if (error) {
        const { type, code, message } = error;
        throw { type, code, message };
      }
    }
  }

  private paymentSuccess(subscription: subscriptions.ISubscription) {
    this.paymentComplete = true;
    this.loading = false;
    this.analyticsService.makePurchase(
      subscription.id,
      subscription.metadata.planKey,
      subscription.metadata.totalPrice,
    );
    this.localizationService.goToProfile();
  }

  private paymentError(msg: string, details?: any) {
    this.loading = false;
    this.paymentErrors = msg;
    console.log('paymentError', details);
  }
}
